import GlobalStyles from '@mui/material/GlobalStyles';
import { styled, ThemeProvider } from '@mui/material/styles';
import { LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { GoogleOAuthProvider } from '@react-oauth/google';
import * as Sentry from '@sentry/react';
import { MaterialDesignContent, SnackbarProvider } from 'notistack';
import { BrowserRouter } from 'react-router-dom';
import validate from 'validate.js';
import { APP_ENVIRONMENT, SENTRY_DSN, SENTRY_ENVIRONMENTS } from 'common/config/app';
import { CookieConsentProvider, ThemeModeProvider, ZoomProvider } from 'common/providers';
import reducers from 'common/reducers';
import appInitialStates from 'common/states';
import { StoreProvider } from 'common/store';
import validators from 'common/validators';
import { ErrorBoundary, SnackCloseIcon } from 'components';
import DialogWrapper from 'components/DialogWrapper';
import ShortcutHelper from 'components/ShortcutHelper/ShortcutHelper';
import { UnsavedChangesWrapper } from 'components/UnsavedChanges';
import { EventBusProvider, TitleProvider, TitleUpdater } from 'context';
import { AppAblyProvider } from 'context/AblyContext';
import { Provider as DashboardProvider } from 'dashboard409a/states/provider';
import { PdfExportProvider } from 'features/pdfExport';
import AuthSessionBridge from 'pages/Auth/components/AuthSessionBridge';
import { AuthSessionRestoreProvider, FeaturesProvider, PersistedQueryProvider } from 'providers';
import Routes from 'Routes';
import AnalyticsProvider from 'scalarAnalytics/AnalyticsProvider';
import AppLoadAsyncOperations from 'services/hooks/AppLoadAsyncOperations';
import theme from 'theme';
import { getStringValue } from 'utilities';
import 'react-perfect-scrollbar/dist/css/styles.css';
import 'assets/scss/index.scss';
import { AsyncProcessorProvider } from './context/AsyncProcessorContext/AsyncProcessorContext';

const PREFIX = 'App';

const classes = {
  snackbarContainerTopRight: `${PREFIX}-snackbarContainerTopRight`,
};

// Toasts sit below the top navigation. The offset goes on the container, which is the fixed element
// that owns the hit box, so the toast cannot paint low while still covering the navigation buttons.
const snackbarContainerStyles = {
  [`.notistack-SnackbarContainer.${classes.snackbarContainerTopRight}`]: {
    top: '155px',
  },
};

const infoPalette = theme.palette.info as typeof theme.palette.primary;

// Add custom validators
validate.validators = {
  ...validate.validators,
  ...validators,
};

// Sentry
Sentry.init({
  environment: APP_ENVIRONMENT,
  dsn: SENTRY_DSN,
  integrations: [
    // Simplified integration for now
  ],
  tracePropagationTargets: [window.location.hostname, getStringValue(process.env.REACT_APP_API_ENDPOINT)],
  beforeSend(event) {
    // Only track errors from staging and production
    if (!SENTRY_ENVIRONMENTS.includes(APP_ENVIRONMENT || '')) {
      return null;
    }
    return event;
  },
  tracesSampleRate: 1.0,
  replaysOnErrorSampleRate: 1.0, // Record all the sampling sessions where errors occur
});

// Notistack Components override
const StyledMaterialDesignContent = styled(MaterialDesignContent)(() => ({
  '&.notistack-MuiContent-success': {
    backgroundColor: theme.palette.secondary.main,
    color: theme.palette.secondary.contrastText,
  },
  '&.notistack-MuiContent-error': {
    backgroundColor: theme.palette.error.main,
    color: theme.palette.error.contrastText,
  },
  '&.notistack-MuiContent-warning': {
    backgroundColor: theme.palette.warning.main,
    color: theme.palette.warning.contrastText,
  },
  '&.notistack-MuiContent-info': {
    backgroundColor: infoPalette.main,
    color: infoPalette.contrastText,
  },
}));

const App = () => (
  <GoogleOAuthProvider clientId={`${process.env.REACT_APP_GOOGLE_CLIENT_ID}`}>
    <AsyncProcessorProvider>
      <StoreProvider initialState={appInitialStates} reducer={reducers}>
        <PersistedQueryProvider>
          <DashboardProvider>
            <EventBusProvider>
              <ThemeModeProvider>
                <ThemeProvider theme={theme}>
                  <ZoomProvider>
                    <BrowserRouter>
                      <CookieConsentProvider>
                        <TitleProvider>
                          <TitleUpdater />
                          <AnalyticsProvider>
                            <ErrorBoundary>
                              <DialogWrapper>
                                <UnsavedChangesWrapper>
                                  <SnackbarProvider
                                    action={SnackCloseIcon}
                                    anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
                                    autoHideDuration={3000}
                                    classes={{ containerAnchorOriginTopRight: classes.snackbarContainerTopRight }}
                                    Components={{ success: StyledMaterialDesignContent }}
                                    maxSnack={3}
                                    preventDuplicate>
                                    <GlobalStyles styles={snackbarContainerStyles} />
                                    <LocalizationProvider dateAdapter={AdapterDateFns}>
                                      <FeaturesProvider>
                                        <AuthSessionRestoreProvider>
                                          <AppAblyProvider>
                                            <AuthSessionBridge />
                                            <AppLoadAsyncOperations />
                                            <PdfExportProvider>
                                              <ShortcutHelper>
                                                <Routes />
                                              </ShortcutHelper>
                                            </PdfExportProvider>
                                          </AppAblyProvider>
                                        </AuthSessionRestoreProvider>
                                      </FeaturesProvider>
                                    </LocalizationProvider>
                                  </SnackbarProvider>
                                </UnsavedChangesWrapper>
                              </DialogWrapper>
                            </ErrorBoundary>
                          </AnalyticsProvider>
                        </TitleProvider>
                      </CookieConsentProvider>
                    </BrowserRouter>
                  </ZoomProvider>
                </ThemeProvider>
              </ThemeModeProvider>
            </EventBusProvider>
          </DashboardProvider>
        </PersistedQueryProvider>
      </StoreProvider>
    </AsyncProcessorProvider>
  </GoogleOAuthProvider>
);

export default Sentry.withProfiler(App);
